import { Color3 } from "@babylonjs/core";
import { logger } from "../../core/Logger.js";

/**
 * @typedef {Object} FloorTheme
 * @property {number} ambientIntensity
 * @property {number} fogDensity
 * @property {number} emissiveMultiplier
 */

/**
 * @typedef {Object} FloorData
 * @property {number} id
 * @property {string} name
 * @property {Color3} color
 * @property {number} rooms
 * @property {string} roomType
 * @property {string} philosophy
 * @property {FloorTheme} theme
 */

/**
 * @class FloorConfig
 * @description Configuration statique des étages du donjon. Contient les thèmes, budgets et paramètres visuels.
 */
export class FloorConfig {
    /** @type {FloorData[]} */
    static FLOORS = [
        {
            id: 1, name: "Interface", color: new Color3(92/255, 149/255, 255/255), // Cornflower Blue
            rooms: 3, roomType: "simple", philosophy: "CALIBRATION",
            theme: { ambientIntensity: 0.5, fogDensity: 0.015, emissiveMultiplier: 1.5 }
        },
        {
            id: 2, name: "Pare-feu", color: new Color3(193/255, 98/255, 0), // Burnt Caramel
            rooms: 3, roomType: "corridor", philosophy: "PRESSION",
            theme: { ambientIntensity: 0.5, fogDensity: 0.02, emissiveMultiplier: 1.2 }
        },
        {
            id: 3, name: "Buffer", color: new Color3(0.1, 0.7, 0.5), // Cyan-green lumineux
            rooms: 3, roomType: "open", philosophy: "ADAPTATION",
            theme: { ambientIntensity: 0.85, fogDensity: 0.008, emissiveMultiplier: 1.8 }
        },
        {
            id: 4, name: "Nexus", color: new Color3(218/255, 218/255, 217/255), // Alabaster Grey
            rooms: 1, roomType: "arena", philosophy: "RUPTURE",
            theme: { ambientIntensity: 0.55, fogDensity: 0.025, emissiveMultiplier: 1.5 }
        }
    ];

    /**
     * Récupère la configuration d'un étage
     * @param {number} floorNumber - Numéro de l'étage (1-based)
     * @returns {FloorData}
     */
    static getFloor(floorNumber) {
        if (typeof floorNumber !== 'number' || isNaN(floorNumber)) {
            logger.error("LEVEL", "FloorConfig", `Invalid floor number: ${floorNumber}. Defaulting to 1.`);
            return this.FLOORS[0];
        }

        // Clamp index between 0 and length-1
        const index = Math.min(Math.max(floorNumber - 1, 0), this.FLOORS.length - 1);
        const config = this.FLOORS[index];

        // Sanity Check
        this.validate(config);

        return config;
    }

    /**
     * Valide une configuration d'étage
     * @param {FloorData} config 
     * @throws {Error} Si la config est invalide
     */
    static validate(config) {
        if (!config) throw new Error("Floor configuration is missing");
        if (config.id < 1) throw new Error(`Invalid floor ID: ${config.id}`);
        if (config.rooms < 1) throw new Error(`Invalid room count for floor ${config.id}: ${config.rooms}`);
        if (!config.color || !(config.color instanceof Color3)) throw new Error(`Invalid color for floor ${config.id}`);
        if (!config.theme) throw new Error(`Missing theme for floor ${config.id}`);
        
        const { theme } = config;
        if (theme.ambientIntensity < 0 || theme.ambientIntensity > 2) {
            logger.warn("LEVEL", "FloorConfig", `Suspicious ambientIntensity: ${theme.ambientIntensity} for floor ${config.id}`);
        }
    }

    /**
     * @returns {FloorData[]}
     */
    static getAllFloors() {
        return this.FLOORS;
    }

    /**
     * Vérifie si un étage est un étage de Boss
     * @param {number} floorNumber 
     * @returns {boolean}
     */
    static isBossFloor(floorNumber) {
        return floorNumber === 4; // Le boss est à l'étage 4
    }

    /**
     * @param {number} floorNumber 
     * @returns {Color3}
     */
    static getFloorColor(floorNumber) {
        return this.getFloor(floorNumber).color;
    }

    /**
     * @param {number} floorNumber 
     * @returns {number}
     */
    static getRoomCount(floorNumber) {
        return this.getFloor(floorNumber).rooms;
    }
}